import React, { useMemo, useState } from "react";
import {
  Layers,
  Package,
  Clock,
  MapPin,
  Search,
  ArrowRight,
} from "lucide-react";
import { Pagination } from "../../../common/pagination";
import moment from "moment";
import { UIAgGridColumn, UIAgGridTable } from "../../../common/agGridTable";
import ProductTagsModal from "./ProductTagsModal";

const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

type ASNLine = {
  upc: string;
  epc?: string[] | string;
  tagId?: string;
  asn?: string;
  quantity?: number;
  productDescription?: string;
  description?: string;
};

type ASNDataType = {
  asn: string;
  lines: ASNLine[];
};

type SelectedAsnType = {
  status?: string;
  dateTime?: string;
  transferredFromSite?: string;
  transferredFromZone?: string;
  transferredToSite?: string;
  transferredToZone?: string;
};

type ShippingNoticeDetailFullWidthProps = {
  ASNData: ASNDataType;
  selectedAsn: SelectedAsnType;
  loading?: boolean;
};

export default function ShippingNoticeDetailFullWidth({
  ASNData,
  selectedAsn,
  loading = false,
}: ShippingNoticeDetailFullWidthProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  const filteredItems = useMemo(() => {
    const lines = ASNData?.lines || [];
    const search = searchQuery.toLowerCase().trim();

    if (!search) return lines;

    return lines.filter((item) => {
      const upc = item?.upc?.toString()?.toLowerCase() || "";
      const epc = Array.isArray(item?.epc)
        ? item.epc.join(",").toLowerCase()
        : item?.epc?.toString()?.toLowerCase() || "";
      const tagId = item?.tagId?.toString()?.toLowerCase() || "";
      const asn = item?.asn?.toString()?.toLowerCase() || "";
      const description = (item?.productDescription || item?.description || "")
        .toString()
        .toLowerCase();

      return (
        upc.includes(search) ||
        epc.includes(search) ||
        tagId.includes(search) ||
        asn.includes(search) ||
        description.includes(search)
      );
    });
  }, [ASNData, searchQuery]);

  const ALL_COLUMNS = useMemo<UIAgGridColumn<any>[]>(() => {
    return [
      {
        key: "upc",
        title: "UPC",
        dataIndex: "upc",
        minWidth: 180,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "productDescription",
        title: "Product Description",
        dataIndex: "productDescription",
        minWidth: 250,
        flex: 2,
        sorter: true,
        render: (value: unknown, row: any) =>
          value ? String(value) : row.description || "-",
      },
      {
        key: "quantity",
        title: "QTY",
        dataIndex: "quantity",
        minWidth: 120,
        width: 120,
        sorter: true,
        headerClass: 'center-header',
        cellClass: 'center-cell',
        render: (value: unknown) => (value ? String(value) : "0"),
      },
    ];
  }, []);

  const handleRowClick = (row: any) => {
    setSelectedProduct(row);
    setIsProductModalOpen(true);
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-slate-50/50 space-y-2 antialiased">
        {/* Header Skeleton */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <div className="space-y-2">
              <SkeletonBlock className="w-36 h-3" />
              <SkeletonBlock className="w-52 h-6" />
            </div>
            <SkeletonBlock className="w-40 h-8" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 border rounded-xl space-y-3 bg-white">
                <SkeletonBlock className="w-10 h-10 rounded-lg" />
                <SkeletonBlock className="w-28 h-3" />
                <SkeletonBlock className="w-16 h-6" />
              </div>
            ))}
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center">
            <SkeletonBlock className="w-48 h-4" />
            <SkeletonBlock className="w-72 h-10 rounded-lg" />
          </div>

          <div className="p-4 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-3 gap-4 border-b border-slate-100 pb-3"
              >
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-slate-50/50 space-y-2 antialiased">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 bg-[var(--primaryLight)] text-[var(--primary)] rounded-lg border border-[var(--primary)]/10">
              <Layers className="w-5 h-5" />
            </div>

            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                Advanced Shipping Notice{" "}
                {selectedAsn?.status && (
                  <span
                    className={`ml-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                      selectedAsn.status.toLowerCase() === "delivered"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200/60"
                        : "bg-amber-50 text-amber-700 border-amber-200/60"
                    }`}
                  >
                    {selectedAsn.status === "inTransit"
                      ? "In Transit"
                      : selectedAsn.status}
                  </span>
                )}
              </span>

              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-mono font-bold text-slate-900 tracking-tight">
                  {ASNData?.asn || "-"}
                </h2>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-white border border-slate-200 px-3 py-1.5 rounded-lg">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>
              {selectedAsn?.dateTime
                ? moment(selectedAsn.dateTime).format("DD MMM YYYY · hh:mm A")
                : "No Date Provided"}
            </span>
          </div>
        </div>

        {/* Summary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
          <div className="p-5 flex items-center gap-4 bg-white">
            <div className="p-3 bg-slate-50 text-slate-600 rounded-xl border border-slate-100">
              <Package className="w-5 h-5 text-[var(--primary)]" />
            </div>
            <div>
              <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Total Line Items
              </span>
              <span className="text-2xl font-bold text-slate-800">
                {ASNData?.lines?.length || 0}
              </span>
            </div>
          </div>

          <div className="p-5 lg:col-span-2 bg-white flex flex-col justify-between">
            <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Routing & Logistics
            </span>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center relative">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60 relative">
                <span className="absolute top-2 right-3 text-[9px] font-extrabold text-slate-400 border border-slate-200 px-1 rounded bg-white">
                  SRC
                </span>
                <span className="block text-[11px] font-medium text-slate-400 uppercase">
                  Origin Location
                </span>
                <div className="text-sm font-bold text-slate-700 mt-0.5 truncate">
                  {selectedAsn?.transferredFromSite || "N/A"}
                </div>
                <div className="inline-flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  <span>Zone: {selectedAsn?.transferredFromZone || "-"}</span>
                </div>
              </div>

              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 bg-white border border-slate-200 rounded-full p-1 shadow-sm text-slate-400">
                <ArrowRight className="w-3.5 h-3.5" />
              </div>

              <div className="p-3 bg-[var(--primaryExtraLight)] rounded-lg border border-[var(--primary)]/10 relative">
                <span className="absolute top-2 right-3 text-[9px] font-extrabold text-[var(--primary)] border border-[var(--primary)]/20 px-1 rounded bg-white">
                  DST
                </span>
                <span className="block text-[11px] font-medium text-[var(--primary)] uppercase">
                  Destination Location
                </span>
                <div className="text-sm font-bold text-slate-900 mt-0.5 truncate">
                  {selectedAsn?.transferredToSite || "N/A"}
                </div>
                <div className="inline-flex items-center gap-1 text-[11px] text-[var(--primary)] mt-1">
                  <MapPin className="w-3 h-3 text-[var(--primary)]/70" />
                  <span>Zone: {selectedAsn?.transferredToZone || "-"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Table Section */}
      <div className="bg-white rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            Manifest Item Registry ({filteredItems.length} items found)
          </h3>

          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search UPC"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none"
            />
          </div>
        </div>

        <div className="h-[180px] overflow-hidden">
          <UIAgGridTable
            columns={ALL_COLUMNS}
            data={filteredItems || []}
            onRowClick={(row) => handleRowClick(row)}
          />
        </div>

        <div className="p-4 border-t border-slate-100 bg-slate-50/30">
          <Pagination
            current={page}
            onPageChange={setPage}
            pageSize={pageSize}
            total={filteredItems.length}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>
      <ProductTagsModal
        open={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        data={selectedProduct}
      />
    </div>
  );
}
